import { useState } from "react";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { useAuth } from "../../../../modules/auth";
import { useGetProperties } from "../../../base/hooks";
import { ControlType, FetchStatus, IEntity, Property } from "../../../base/types";
import { Registry } from "../../../base/register";
import { UndertakingProperties } from "./properties";
import { useDataContext } from "../../page/context";
import { PageLinkHelper } from "../../../base/helpers";
import { useNavigate } from "react-router-dom";
import { IFieldValue } from "../../../services/models";
import { useUpdateMutation } from "../../../services/data/tableDataApi";

export const UnderTakingComponent = (props: IControlAttributes) => {
  const { control } = props;
  const { properties } = useGetProperties({ bindings: control.bindings });
  const navigateToOnCancel: IPageLink | undefined = properties[Property.CancelPageLink];
  const { setData } = useDataContext();
  const { user, siteId } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const [updateRecord] = useUpdateMutation();

  const [formState, setFormState] = useState({
    agreement1: false,
    agreement2: false,
    agreement3: false,
    agreement4: false,
    agreement5: false,
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!formState.agreement1 || !formState.agreement2 || !formState.agreement3 || !formState.agreement4 || !formState.agreement5) {
      alert("Please agree to all terms before submitting.");
      return;
    }

    const record: IEntity = {
      slcm_undertaking: 407450001,
      new_acknowlegementdate: new Date(),
      new_indemnityacknowledged: true,
    };

    saveFormData(record)
      .then((data) => {
        setData({ type: "Card", result: data, status: FetchStatus.Success });
        if (siteId && navigateToOnCancel && navigateToOnCancel.pageId) {
          navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnCancel));
        }
      })
      .catch((err) => {
        const message =
          err.data?.message || "An error occurred while processing your request. Please try again later.";
        console.error(message);
      })
      .finally(() => setLoading(null));
  };

  const saveFormData = (record: IEntity): Promise<IEntity> => {
    const fields: IFieldValue[] = Object.entries(record)
      .filter(([_, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value, validate: true }));

    return updateRecord({
      portalId: siteId || "",
      userId: user?.contactId,
      tableId: "contact",
      sysId: user?.contactId,
      data: fields,
      triggers: true,
    })
      .unwrap()
      .catch((err) => {
        const message =
          err.data?.message || "An error occurred while processing your request. Please try again later.";
        console.error(message);
        return Promise.reject(message);
      });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="p-4">
        <div className="form-check mb-3">
          <input
            type="checkbox"
            required={true}
            className="form-check-input"
            id="agreement1"
            name="agreement1"
            checked={formState.agreement1}
            onChange={handleCheckboxChange}
          />
          <label className="form-check-label" style={{ fontSize: "10px" }} htmlFor="agreement1">
            I acknowledge that my personal information is complete and accurate for all official documentation and communication purposes. I understand that any future changes must be submitted via a separate service request through the Student Portal.
          </label>
        </div>
        <div className="form-check mb-3">
          <input
            type="checkbox"
            required={true}
            className="form-check-input"
            id="agreement2"
            name="agreement2"
            checked={formState.agreement2}
            onChange={handleCheckboxChange}
          />
          <label className="form-check-label" style={{ fontSize: "10px" }} htmlFor="agreement2">
            I acknowledge the terms set forth in the Student Handbook <b>(refer to Left Menu)</b> and commit to upholding the standards, rules and regulations of MUCM. I understand that this handbook may be updated periodically to reflect current institutional policies and agree to abide by the same.
          </label>
        </div>
        <div className="form-check mb-3">
          <input
            type="checkbox"
            required={true}
            className="form-check-input"
            id="agreement3"
            name="agreement3"
            checked={formState.agreement3}
            onChange={handleCheckboxChange}
          />
          <label className="form-check-label" style={{ fontSize: "10px" }} htmlFor="agreement3">
            I declare that I have read and fully understood the Release and Indemnification Form <b>(refer to Left Menu)</b> for Any Trips, Activities or Excursions in its entirety, and I hereby agree to be bound by the terms and conditions.
          </label>
        </div>
        <div className="form-check mb-3">
          <input
            type="checkbox"
            required={true}
            className="form-check-input"
            id="agreement4"
            name="agreement4"
            checked={formState.agreement4}
            onChange={handleCheckboxChange}
          />
          <label className="form-check-label" style={{ fontSize: "10px" }} htmlFor="agreement4">
            I declare that I am aware and fully understood that by clicking “SUBMIT”, ticking the acceptance box and submitting this form, I am providing my consent and signify that I have read, understood and agree to be legally bound by the terms and conditions of the MUCM Student Handbook & Release and Indemnification Form. I am also aware and understand that I am voluntarily waiving certain legal rights which I, my heirs, next of kin, executors, administrators, personal representatives and/or assigns may have against the University, its directors, officers, employees, agents, representatives, and/or service providers.
          </label>
        </div>
        <div className="form-check mb-3">
          <input
            type="checkbox"
            required={true}
            className="form-check-input"
            id="agreement5"
            name="agreement5"
            checked={formState.agreement5}
            onChange={handleCheckboxChange}
          />
          <label className="form-check-label" style={{ fontSize: "10px" }} htmlFor="agreement5">
            I acknowledge and confirm that I execute consent of MUCM Student Handbook & Release and Indemnification Form for Any Travel, Trips, Activities or Excursions of my own free will, voluntarily and without coercion.
          </label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading !== null}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </>
  );
};

Registry.addControl({
  id: "0d7ac5a6-d3c1-439b-9612-c1001ad48223",
  name: "Undertaking Component",
  type: ControlType.UnderTakingComponent,
  properties: UndertakingProperties,
  component: UnderTakingComponent,
});
